import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Calculator,
  AlertTriangle,
  CheckCircle2,
  Info,
} from "lucide-react";
import { cleanBrandName, formatMoney, formatNumber } from "@/lib/utils";
import { formatBomNorm } from "@/shared/helpers/product-costing.helper";
import { trpcClient } from "@/lib/trpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: any;
  onSuccess?: () => void;
}

export function ProductCostPreviewModal({
  open,
  onOpenChange,
  product,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient();
  const [isApplying, setIsApplying] = useState(false);

  // Đơn giá niêm yết (giá bán) do kinh doanh nhập ở form sản phẩm: modal này chỉ tính và cập nhật giá vốn
  const sellingPrice = Math.round(Number(product?.price || 0));

  const { data: decomp, isLoading } = useQuery({
    queryKey: ["product-decomposition-preview", product?.id],
    queryFn: async () =>
      trpcClient.products.decomposeCost.query({ id: product.id }),
    enabled: open && !!product?.id,
  });

  const batchSize = Number(
    decomp?.targetQuantity || decomp?.calculationBasis?.batchSize || 1000,
  );
  const outs = Number(
    decomp?.outs || decomp?.calculationBasis?.outs || product?.outs || 1,
  );
  const sheetFormat =
    decomp?.calculationBasis?.sheetFormat ||
    product?.printFormat ||
    product?.cutFormat ||
    "Chuẩn";
  const unitLabel = product?.unit || "SP";

  const currentCost = Math.round(Number(product?.costPrice || 0));
  const estimatedCost = Math.round(Number(decomp?.summary?.unitCost ?? 0));
  const batchMaterialCost = Math.round(
    Number(decomp?.summary?.totalMaterialCost ?? 0),
  );
  const batchProcessCost = Math.round(
    Number(decomp?.summary?.totalProcessCost ?? 0),
  );
  const batchOutsourceCost = Math.round(
    Number(decomp?.summary?.totalOutsourceCost ?? 0),
  );
  const batchFixedCost = Math.round(
    Number(decomp?.summary?.totalFixedCost ?? 0),
  );
  const batchTotalCost = Math.round(
    Number(
      decomp?.summary?.totalCost ??
        batchMaterialCost +
          batchProcessCost +
          batchOutsourceCost +
          batchFixedCost,
    ),
  );

  const isLossMaking = sellingPrice > 0 && estimatedCost > sellingPrice;
  const diffCost = estimatedCost - currentCost;

  const materialRows = useMemo(() => {
    if (!Array.isArray(decomp?.components)) return [];
    return decomp.components.flatMap((comp: any) =>
      (comp.materials || []).map((m: any) => ({
        ...m,
        componentName: comp.componentName,
      })),
    );
  }, [decomp?.components]);

  const routingRows = useMemo(() => {
    if (!Array.isArray(decomp?.components)) return [];
    return decomp.components.flatMap((comp: any) =>
      (comp.routings || []).map((r: any) => ({
        ...r,
        componentName: comp.componentName,
      })),
    );
  }, [decomp?.components]);

  const handleConfirmRecalculate = async () => {
    try {
      setIsApplying(true);
      const res = await trpcClient.products.recalculateCostPrice.mutate({
        id: product.id,
        batchSize,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["product", product.id] }),
        queryClient.invalidateQueries({
          queryKey: ["product-decomposition", product.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["product-decomposition-preview", product.id],
        }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["products-lookup"] }),
        queryClient.invalidateQueries({
          queryKey: ["entity-select", "products"],
        }),
        queryClient.invalidateQueries({
          predicate: (q) => JSON.stringify(q.queryKey).includes("combobox"),
        }),
      ]);
      toast.success(
        res?.message ||
          "Đã cập nhật giá vốn chuẩn xác từ định mức thành công!",
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi cập nhật giá vốn");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-cost-preview-modal"
        className="sm:max-w-[680px] max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-emerald-600" />
            Dự tính & Đối soát Giá vốn chuẩn
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <span className="text-xs text-slate-500">
              Đang bóc tách định mức vật tư và công đoạn...
            </span>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            {/* 1. So sánh giá cũ vs mới */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div>
                <span className="text-[11px] text-slate-500 block mb-0.5">
                  Giá vốn hiện tại
                </span>
                <span className="text-sm font-semibold text-slate-700 font-mono">
                  {formatMoney(currentCost)} đ
                </span>
              </div>
              <div>
                <span className="text-[11px] text-slate-500 block mb-0.5">
                  Giá vốn dự tính (1 {unitLabel})
                </span>
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span
                    data-testid="estimated-unit-cost-value"
                    className="text-base font-bold text-emerald-700 font-mono"
                  >
                    {formatMoney(estimatedCost)} đ
                  </span>
                  {diffCost !== 0 && (
                    <span
                      className={`text-[10px] font-semibold font-mono ${
                        diffCost > 0 ? "text-amber-600" : "text-emerald-600"
                      }`}
                    >
                      {diffCost > 0
                        ? `+${formatMoney(diffCost)} đ`
                        : `${formatMoney(diffCost)} đ`}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <span className="text-[11px] text-slate-500 block mb-0.5">
                  Đơn giá niêm yết (Catalog)
                </span>
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-indigo-700 font-mono">
                    {sellingPrice > 0 ? `${formatMoney(sellingPrice)} đ` : "Chưa nhập"}
                  </span>
                </div>
              </div>
            </div>

            {/* 2. Cảnh báo biên lợi nhuận âm (chỉ khi Giá vốn > Giá bán thực tế) */}
            {isLossMaking && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2.5">
                <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h5 className="text-xs font-bold text-rose-800">
                    CẢNH BÁO: GIÁ VỐN DỰ TÍNH ĐANG CAO HƠN ĐƠN GIÁ BÁN
                  </h5>
                  <p className="text-[11px] text-rose-700 leading-relaxed">
                    Giá vốn dự tính ({formatMoney(estimatedCost)} đ) đang cao
                    hơn đơn giá niêm yết ({formatMoney(sellingPrice)} đ). Vui
                    lòng kiểm tra lại định mức; đơn giá niêm yết do kinh doanh
                    nhập ở form sản phẩm.
                  </p>
                </div>
              </div>
            )}

            {/* 3. Diễn giải công thức & chi tiết cấu thành lô chuẩn */}
            <div
              data-testid="cost-calculation-explanation"
              className="rounded-lg border border-slate-200 overflow-hidden bg-white"
            >
              <div className="px-3.5 py-2.5 bg-indigo-50/70 border-b border-indigo-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-900">
                  <Info className="h-4 w-4 text-indigo-600 shrink-0" />
                  <span>
                    Cách tính giá vốn (theo lô chuẩn {formatNumber(batchSize)}{" "}
                    {unitLabel})
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="bg-white text-[11px] border-indigo-200 text-indigo-800 font-medium"
                  >
                    Số bát: {outs} con/tờ
                  </Badge>
                  <Badge
                    variant="outline"
                    className="bg-white text-[11px] border-slate-200 text-slate-700 font-medium"
                  >
                    Khổ giấy: {sheetFormat}
                  </Badge>
                </div>
              </div>

              <div className="divide-y divide-slate-100 text-xs">
                {/* Nhóm 1: Nguyên vật liệu */}
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between font-semibold text-slate-800">
                    <span>
                      1. Chi phí Nguyên vật liệu (lô {formatNumber(batchSize)}{" "}
                      {unitLabel})
                    </span>
                    <span className="font-mono text-blue-700">
                      {formatMoney(batchMaterialCost)} đ
                    </span>
                  </div>
                  {materialRows.length > 0 ? (
                    <div className="space-y-1.5 pl-2 border-l-2 border-blue-200">
                      {materialRows.map((m: any, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-start justify-between gap-3 text-[11px]"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-800">
                              {cleanBrandName(m.materialName)}{" "}
                              <span className="text-slate-500 font-normal">
                                (Định mức: {formatBomNorm(m.standardQuantity)}{" "}
                                {m.unit || "tờ"}/{unitLabel})
                              </span>
                            </div>
                            <div className="text-slate-500 leading-snug">
                              {m.formulaExplanation || m.note || "Tính theo định mức chuẩn"}
                            </div>
                          </div>
                          <span className="font-mono font-semibold text-slate-700 shrink-0">
                            {formatMoney(m.calculatedCost)} đ
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic pl-2">
                      Chưa khai báo vật tư cho sản phẩm này.
                    </p>
                  )}
                </div>

                {/* Nhóm 2: Công đoạn sản xuất */}
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between font-semibold text-slate-800">
                    <span>
                      2. Chi phí Công đoạn sản xuất (lô{" "}
                      {formatNumber(batchSize)} {unitLabel})
                    </span>
                    <span className="font-mono text-indigo-700">
                      {formatMoney(batchProcessCost)} đ
                    </span>
                  </div>
                  {routingRows.length > 0 ? (
                    <div className="space-y-1.5 pl-2 border-l-2 border-indigo-200">
                      {routingRows.map((r: any, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-start justify-between gap-3 text-[11px]"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-800">
                              {r.processName}
                            </div>
                            <div className="text-slate-500 leading-snug">
                              {r.formulaExplanation ||
                                `Phí lên bài ${formatMoney(r.setupCost)} đ + chạy máy ${formatMoney(r.unitCost)} đ`}
                            </div>
                          </div>
                          <span className="font-mono font-semibold text-slate-700 shrink-0">
                            {formatMoney(r.calculatedCost)} đ
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic pl-2">
                      Chưa khai báo công đoạn cho sản phẩm này.
                    </p>
                  )}
                </div>

                {/* Nhóm 3: Phí khuôn bản / Gia công ngoài cộng thêm (nếu tách ngoài công đoạn) hoặc ghi chú cấu phần cố định */}
                {(() => {
                  const extraAdditiveCost = Math.max(
                    0,
                    batchTotalCost - (batchMaterialCost + batchProcessCost),
                  );
                  if (extraAdditiveCost > 0) {
                    return (
                      <div className="px-3 py-2 flex items-center justify-between text-slate-700">
                        <span className="font-medium">
                          3. Phí khuôn bản & Gia công ngoài cộng thêm
                        </span>
                        <span className="font-mono font-semibold">
                          {formatMoney(extraAdditiveCost)} đ
                        </span>
                      </div>
                    );
                  }
                  if (batchFixedCost > 0) {
                    return (
                      <div className="px-3 py-1.5 flex items-center justify-between text-[11px] text-slate-500 bg-slate-50/60">
                        <span>
                          Lưu ý: Chi phí công đoạn đã bao gồm phí kẽm & lên bài cố định
                        </span>
                        <span className="font-mono font-medium text-slate-600">
                          {formatMoney(batchFixedCost)} đ
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Tổng kết công thức quy đổi về 1 SP */}
                <div className="p-3 bg-emerald-50/60 space-y-1">
                  <div className="flex items-center justify-between text-slate-700">
                    <span>
                      Tổng chi phí cả lô ({formatNumber(batchSize)} {unitLabel})
                    </span>
                    <span className="font-mono font-semibold text-slate-900">
                      {formatMoney(batchTotalCost)} đ
                    </span>
                  </div>
                  <div className="flex items-center justify-between font-semibold text-emerald-800 pt-1 border-t border-emerald-200/70">
                    <span>
                      Giá vốn 1 {unitLabel} = {formatMoney(batchTotalCost)} đ ÷{" "}
                      {formatNumber(batchSize)} {unitLabel}
                    </span>
                    <span className="font-mono text-sm font-bold text-emerald-700">
                      {formatMoney(estimatedCost)} đ / {unitLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Giá bán không do tính giá quyết định */}
            <p
              className="text-[11px] text-slate-600 leading-relaxed"
              data-testid="product-cost-price-notice"
            >
              Xác nhận chỉ cập nhật Giá vốn chuẩn. Đơn giá niêm yết (giá bán) do
              kinh doanh nhập ở form sản phẩm và không bị thay đổi; tính giá
              đang phát triển, kết quả chỉ để tham khảo.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isApplying}
          >
            Đóng
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
            onClick={handleConfirmRecalculate}
            disabled={isLoading || isApplying || estimatedCost <= 0}
          >
            {isApplying ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Đang cập nhật...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                Xác nhận cập nhật giá vốn
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
